// Copyright (c) 2017+,COMUNICARE SOLUTIONS SA
// All rights reserved.
//
// Redistribution and use in source and binary forms, with or without modification,
// are NOT permitted.

import { Reference, Identifier, IEntity, STATUS_ENTITY, ACTION_STATUS_ENTITY } from "../../models/sharedInterfaces";
import { IRuleDefinition, IRuleMeta, IRule } from "../../models/rule";
import { NOTIFICATION_STATUS } from "../../models/notification";
import { IObservation, OComponent, IObservationWithoutComponents } from "../observation-helper";
import * as moment from "moment";
import { FHIRHelper } from "../FHIR-helper";
import { Tools } from "../tools-helper";
import { Account, IAccount } from "../account-helper";
import { QuestionnaireResponse } from "../questionnaireResponse";
import { v4 as uuidv4 } from "uuid";

/**********************************************************************************
 *
 * Interface for Rule Alert
 *
 */
export interface IRuleAlert extends IEntity {
  identifier: Identifier[];
  subject: Reference; // patient reference for whom the alert was generated
  ruleMeta: IRuleMeta; // Meta data info on Rule
  rule: IRuleDefinition; // rule trigerred
  valueComponents?: OComponent[]; // component value entered,
  observation?: IObservationWithoutComponents; // observation that generated the alert
  status?: NOTIFICATION_STATUS; // status of the treatment of this alert by the patient
  comment?: string; // comment of the patient
  questionnaireResponse?: QuestionnaireResponse;
}

/**
 * Helper for rule alert
 */
export class RuleAlertHelper {
  /**
   * Create a Rule Alert from a rule and definition
   * @param rule
   * @param ruleDefinition
   */
  public static asRuleAlertForObservation(
    rule: IRule,
    ruleDefinition: IRuleDefinition,
    currentObs: IObservation,
    user: IAccount
  ): IRuleAlert {
    const obsCopy = Tools.deepCopy(currentObs);
    delete obsCopy.component;
    delete obsCopy.linkedRuleAlerts; // there is no need to enter this information again, as the observation is copied to the ruleAlert
    const obsWithoutComponents: IObservationWithoutComponents = obsCopy;
    const alert: IRuleAlert = {
      _id: undefined,
      creation: moment().format(),
      modified: moment().format(),
      entityStatus: [STATUS_ENTITY.ACTIVE],
      actionStatus: ACTION_STATUS_ENTITY.CREATED,
      identifier: [
        {
          system: FHIRHelper.SYSTEM_CAREMATE,
          value: rule.identifier.value + "/" + uuidv4(),
        },
      ],
      subject: Account.account2Reference(user),
      ruleMeta: rule.meta,
      rule: ruleDefinition,
      valueComponents: currentObs.component,
      observation: obsWithoutComponents,
      status: NOTIFICATION_STATUS.NONE,
      comment: null,
    };
    return Tools.deepCopy(alert);
  }

  /**
   * Create a Rule Alert from a rule and definition
   * @param rule
   * @param ruleDefinition
   */
  public static asRuleAlertForQuestionnaire(
    rule: IRule,
    ruleDefinition: IRuleDefinition,
    currentQuestionnaire: QuestionnaireResponse,
    user: IAccount
  ): IRuleAlert {
    const alert: IRuleAlert = {
      _id: undefined,
      creation: moment().format(),
      modified: moment().format(),
      entityStatus: [STATUS_ENTITY.ACTIVE],
      actionStatus: ACTION_STATUS_ENTITY.CREATED,
      identifier: [
        {
          system: FHIRHelper.SYSTEM_CAREMATE,
          value: rule.identifier.value + "/" + uuidv4(),
        },
      ],
      subject: Account.account2Reference(user),
      ruleMeta: rule.meta,
      rule: ruleDefinition,
      questionnaireResponse: currentQuestionnaire,
      status: NOTIFICATION_STATUS.NONE,
      comment: null,
    };
    return Tools.deepCopy(alert);
  }

  public static asRuleAlertForDrug(rule: IRule, ruleDefinition: IRuleDefinition, user: IAccount): IRuleAlert {
    const alert: IRuleAlert = {
      _id: undefined,
      creation: moment().format(),
      modified: moment().format(),
      entityStatus: [STATUS_ENTITY.ACTIVE],
      actionStatus: ACTION_STATUS_ENTITY.CREATED,
      identifier: [
        {
          system: FHIRHelper.SYSTEM_CAREMATE,
          value: rule.identifier.value + "/" + uuidv4(),
        },
      ],
      subject: Account.account2Reference(user),
      ruleMeta: rule.meta,
      rule: ruleDefinition,
      // observation: obsWithoutComponents, TODO
      // questionnaireResponse: currentQuestionnaire
      status: NOTIFICATION_STATUS.NONE,
      comment: null,
    };
    return Tools.deepCopy(alert);
  }
}
